import { z } from "zod";

const CartPayloadSchema = z
  .object({
    cartContext: z
      .object({
        cartInstanceId: z.string(),
        pageReferrer: z.string(),
      })
      .strict(),
    referrer: z.string(),
  })
  .strict();

const ElementPayloadSchema = z
  .object({
    actionOutcome: z.string(),
    correlationId: z.string().optional(),
    outboundUrl: z.string().optional(),
    webElement: z
      .object({
        elementType: z.string(),
        location: z.string().optional(),
        name: z.string().optional(),
        position: z.string().optional(),
        text: z.string().optional(),
      })
      .strict(),
  })
  .strict();

const ElementViewedPayloadSchema = z
  .object({
    correlationId: z.string().optional(),
    webElement: z
      .object({
        elementType: z.string(),
        location: z.string(),
        name: z.string().optional(),
        position: z.string().optional(),
        text: z.string().optional(),
      })
      .strict(),
  })
  .strict();

const FormPayloadSchema = z
  .object({
    captureContext: z
      .object({
        capturePlacement: z.enum([
          "Inline",
          "Banner",
          "Modal",
          "Full Screen",
          "Journey",
          "inline",
          "floating",
          "popup",
          "Filo message",
          "slide",
        ]),
        captureType: z.enum([
          "OptinMonster",
          "Custom Form",
          "Flow",
          "MyFinance",
          "Facebook Lead",
          "Filo",
          "Formal Onboarding",
          "Continuous Onboarding",
        ]),
        identityRequested: z.boolean(),
      })
      .optional(),
    formContext: z
      .object({
        formId: z.string(),
        formName: z.string(),
        formType: z.string(),
        formBrand: z.string().optional(),
      })
      .strict(),
    field: z
      .array(
        z.object({
          fieldName: z.string(),
          fieldValue: z.string(),
        })
      )
      .optional(),
  })
  .strict();

const ProductPayloadSchema = z
  .object({
    actionOutcome: z.string().optional(),
    correlationId: z.string().optional(),
    outboundUrl: z.string().optional(),
    product: z
      .object({
        location: z.string(),
        text: z.string().optional(),
        name: z.string().optional(),
        position: z.number().optional(),
        brand: z.string().optional(),
        variant: z.string().optional(),
        sku: z.string().optional(),
        price: z.number().optional(),
        productId: z.string().optional(),
        quantity: z.number().optional(),
      })
      .strict(),
  })
  .strict();

const SearchPayloadSchema = z
  .object({
    rawSearchQuery: z.string().optional(),
    searchAutoSuggested: z.boolean().optional(),
  })
  .strict();

const SignUpPayloadSchema = z
  .object({
    customerId: z.string().optional(),
    signUpType: z.enum(["Newsletter", "Push", "SMS"]),
    formId: z.string().optional(),
    hashedEmail: z.string().optional(),
    email: z.string().optional(),
    campaignSignedUp: z.string().optional(),
    subscriptionPreference: z.enum(["Subscribed", "Unsubscribed"]),
    captureContext: z
      .object({
        capturePlacement: z.enum([
          "Inline",
          "Banner",
          "Modal",
          "Full Screen",
          "Journey",
          "inline",
          "floating",
          "popup",
          "Filo message",
          "slide",
        ]),
        captureType: z.enum([
          "OptinMonster",
          "Custom Form",
          "Flow",
          "MyFinance",
          "Facebook Lead",
          "Filo",
          "Formal Onboarding",
          "Continuous Onboarding",
        ]),
        identityRequested: z.boolean(),
        valueProp: z.string().optional(),
      })
      .strict(),
  })
  .strict();

export const EventDefinitions = {
  CartStarted: {
    eventType: "redventures.ecommerce.v1.CartStarted",
    payload: CartPayloadSchema,
  },
  ElementClicked: {
    eventType: "redventures.usertracking.v3.ElementClicked",
    payload: ElementPayloadSchema,
  },
  ElementSwiped: {
    eventType: "redventures.usertracking.v3.ElementSwiped",
    payload: ElementPayloadSchema,
  },
  ElementViewed: {
    eventType: "redventures.usertracking.v3.ElementViewed",
    payload: ElementViewedPayloadSchema,
  },
  FormStarted: {
    eventType: "redventures.usertracking.v3.FormStarted",
    payload: FormPayloadSchema,
  },
  FormSubmitted: {
    eventType: "redventures.usertracking.v3.FormSubmitted",
    payload: FormPayloadSchema,
  },
  FormViewed: {
    eventType: "redventures.usertracking.v3.FormViewed",
    payload: FormPayloadSchema,
  },
  ProductClicked: {
    eventType: "redventures.ecommerce.v1.ProductClicked",
    payload: ProductPayloadSchema,
  },
  ProductLoaded: {
    eventType: "redventures.ecommerce.v1.ProductLoaded",
    payload: ProductPayloadSchema,
  },
  ProductViewed: {
    eventType: "redventures.ecommerce.v1.ProductViewed",
    payload: ProductPayloadSchema,
  },
  Search: {
    eventType: "redventures.usertracking.v1.SearchQuerySubmitted",
    payload: SearchPayloadSchema,
  },
  SignUpCaptured: {
    eventType: "core.SignUpCaptured.v2",
    payload: SignUpPayloadSchema,
  },
};

export type EventPayloads = {
  [K in keyof typeof EventDefinitions]: z.infer<
    (typeof EventDefinitions)[K]["payload"]
  >;
};
